import React, { useState } from 'react';
import { LayoutDashboard, Building2, Users, Calendar, MessageSquare, Trophy, Settings, Globe, Network, ChevronDown, ChevronRight, Bell, X } from 'lucide-react';
import { Tenant } from '../../types';
interface SidebarProps {
  currentView: 'admin' | 'club';
  currentPage: string;
  onNavigate: (page: string) => void;
  onToggleView: () => void;
  tenant?: Tenant;
  isOpen: boolean;
  onClose: () => void;
}
export function Sidebar({
  currentView,
  currentPage,
  onNavigate,
  onToggleView,
  tenant,
  isOpen,
  onClose
}: SidebarProps) {
  const [networkExpanded, setNetworkExpanded] = useState(true);
  const adminNav = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    id: 'tenants',
    label: 'Tenants',
    icon: Globe
  }];
  const clubNav = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    id: 'network',
    label: 'Netzwerk',
    icon: Network,
    subItems: [{
      id: 'network-companies',
      label: 'Unternehmen',
      icon: Building2
    }, {
      id: 'network-people',
      label: 'Personen',
      icon: Users
    }]
  }, {
    id: 'events',
    label: 'Events',
    icon: Calendar
  }, {
    id: 'feed',
    label: 'Feed',
    icon: MessageSquare
  }, {
    id: 'messages',
    label: 'Nachrichten',
    icon: Bell,
    badge: 3
  }, {
    id: 'challenges',
    label: 'Challenges',
    icon: Trophy
  }, {
    id: 'settings',
    label: 'Einstellungen',
    icon: Settings
  }];
  const navItems = currentView === 'admin' ? adminNav : clubNav;
  const handleNavigate = (page: string) => {
    onNavigate(page);
    onClose();
  };
  return <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1a1a2e] text-slate-300 flex flex-col h-screen border-r border-slate-800 flex-shrink-0 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-43">
      {/* Logo & View Toggle */}
      <div className="p-6 border-b border-slate-800 relative" data-id="element-44">
        <button onClick={onClose} className="absolute top-4 right-4 lg:hidden text-slate-400 hover:text-white" data-id="element-45">
          <X size={20} data-id="element-46" />
        </button>
        <div className="flex items-center gap-3 mb-6" data-id="element-47">
          <div className="w-8 h-8 rounded bg-emerald-500 flex items-center justify-center text-white font-bold text-xl" data-id="element-48">
            C
          </div>
          <span className="text-xl font-bold text-white tracking-wide" data-id="element-49">
            Connectra
          </span>
        </div>

        <button onClick={onToggleView} className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800/50 hover:bg-slate-800 text-sm transition-colors border border-slate-700" data-id="element-50">
          <span className="font-medium text-slate-200" data-id="element-51">
            {currentView === 'admin' ? 'System Admin' : 'Club Ansicht'}
          </span>
          <ChevronDown size={16} className="text-slate-400" data-id="element-52" />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-6 px-3 space-y-1" data-id="element-53">
        {currentView === 'club' && tenant && <div className="mb-6 px-3 flex items-center gap-3" data-id="element-54">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl shadow-lg" style={{
          backgroundColor: tenant.primaryColor
        }} data-id="element-55">
              {tenant.logo}
            </div>
            <div data-id="element-56">
              <div className="text-white font-semibold text-sm leading-tight" data-id="element-57">
                {tenant.name}
              </div>
              <div className="text-slate-400 text-xs" data-id="element-58">{tenant.league}</div>
            </div>
          </div>}

        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 px-3" data-id="element-59">
          Menü
        </div>

        {navItems.map(item => {
        if (item.subItems) {
          return <div key={item.id} className="space-y-1" data-id="element-60">
                <button onClick={() => setNetworkExpanded(!networkExpanded)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${currentPage.startsWith(item.id) ? 'text-white' : 'hover:bg-slate-800/50 hover:text-white'}`} data-id="element-61">
                  <div className="flex items-center gap-3" data-id="element-62">
                    <item.icon size={20} className={currentPage.startsWith(item.id) ? 'text-emerald-400' : 'text-slate-400'} data-id="element-63" />
                    <span className="font-medium" data-id="element-64">{item.label}</span>
                  </div>
                  {networkExpanded ? <ChevronDown size={16} data-id="element-65" /> : <ChevronRight size={16} data-id="element-66" />}
                </button>

                {networkExpanded && <div className="pl-10 pr-2 space-y-1 mt-1" data-id="element-67">
                    {item.subItems.map(subItem => <button key={subItem.id} onClick={() => handleNavigate(subItem.id)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${currentPage === subItem.id ? 'bg-emerald-500/10 text-emerald-400 font-medium' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`} data-id="element-68">
                        <subItem.icon size={16} data-id="element-69" />
                        {subItem.label}
                      </button>)}
                  </div>}
              </div>;
        }
        return <button key={item.id} onClick={() => handleNavigate(item.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${currentPage === item.id ? 'bg-emerald-500/10 text-emerald-400 font-medium' : 'hover:bg-slate-800/50 hover:text-white'}`} data-id="element-70">
              <div className="flex items-center gap-3" data-id="element-71">
                <item.icon size={20} className={currentPage === item.id ? 'text-emerald-400' : 'text-slate-400'} data-id="element-72" />
                <span className="font-medium" data-id="element-73">{item.label}</span>
              </div>
              {item.badge && <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-74">
                  {item.badge}
                </span>}
            </button>;
      })}
      </div>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-800" data-id="element-75">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-800/50 cursor-pointer transition-colors" data-id="element-76">
          <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-white" data-id="element-77">
            AD
          </div>
          <div className="flex-1 min-w-0" data-id="element-78">
            <p className="text-sm font-medium text-white truncate" data-id="element-79">
              Admin User
            </p>
            <p className="text-xs text-slate-400 truncate" data-id="element-80">
              admin@connectra.io
            </p>
          </div>
        </div>
      </div>
    </div>;
}